import { useEffect, useRef } from "react";
import { Scissors, Trash2, Copy, GripVertical } from "lucide-react";
import { fmtTime } from "@/lib/filters";

function tickInterval(pxs) {
    if (pxs >= 120) return 0.5;
    if (pxs >= 60) return 1;
    if (pxs >= 24) return 5;
    return 10;
}

export default function Timeline({
    clips,
    duration,
    zoom,
    selectedId,
    playheadRef,
    onSeek,
    onSelect,
    onTrim,
    onReorder,
    onSplit,
    onDelete,
    onDuplicate,
}) {
    const scrollRef = useRef(null);
    const contentRef = useRef(null);
    const headElRef = useRef(null);
    const dragRef = useRef(null);

    const pxs = Math.round(56 * zoom);

    useEffect(() => {
        playheadRef.current.el = headElRef.current;
    });

    const timeAt = (clientX) => {
        const el = contentRef.current;
        if (!el) return 0;
        const r = el.getBoundingClientRect();
        return Math.max(0, Math.min(duration, (clientX - r.left) / pxs));
    };

    const startSeek = (e) => {
        e.stopPropagation();
        dragRef.current = { mode: "seek" };
        e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
        onSeek(timeAt(e.clientX));
    };
    const startTrim = (e, idx, side) => {
        e.stopPropagation();
        dragRef.current = { mode: "trim", idx, side, startX: e.clientX, clip: clips[idx] };
        e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
    };
    const startMove = (e, idx) => {
        e.stopPropagation();
        dragRef.current = { mode: "move", idx, startX: e.clientX, dx: 0, el: e.currentTarget.firstChild };
        onSelect(clips[idx].id);
    };

    const onMove = (e) => {
        const d = dragRef.current;
        if (!d) return;
        if (d.mode === "seek") {
            onSeek(timeAt(e.clientX));
        } else if (d.mode === "trim") {
            const dt = (e.clientX - d.startX) / pxs;
            onTrim(d.idx, d.side, dt, d.clip);
        } else if (d.mode === "move") {
            d.dx = e.clientX - d.startX;
            if (d.el) d.el.style.transform = `translateX(${d.dx}px)`;
        }
    };

    const onUp = (e) => {
        const d = dragRef.current;
        dragRef.current = null;
        if (!d) return;
        if (d.mode === "move") {
            const clipW = (clips[d.idx] ? Math.max(0.3, clips[d.idx].trimEnd - clips[d.idx].trimStart) * pxs : 0) + 6;
            const shift = Math.round(d.dx / clipW);
            if (d.el) d.el.style.transform = "";
            if (shift !== 0) onReorder(d.idx, d.idx + shift);
        }
    };

    const interval = tickInterval(pxs);
    const ticks = [];
    for (let t = 0; t <= duration + 0.001; t += interval) {
        ticks.push(t);
    }

    if (!clips.length) {
        return (
            <div className="border-t border-white/10 bg-surface/40 px-4 py-6 text-center">
                <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500">TIMELINE</p>
                <p className="mt-2 text-sm text-slate-400">Import a video or add a sample to start editing.</p>
            </div>
        );
    }

    let cum = 0;

    return (
        <div className="border-t border-white/10 bg-surface/40">
            <div className="flex items-center justify-between px-3 pt-2">
                <span className="font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">TIMELINE</span>
                <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[10px] text-slate-500">{fmtTime(duration)}</span>
                    {selectedId && (
                        <>
                            <button
                                data-testid="timeline-split-button"
                                onClick={onSplit}
                                title="Split at playhead"
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/15 text-slate-200 hover:border-amberx hover:text-amberx"
                            >
                                <Scissors className="h-3.5 w-3.5" />
                            </button>
                            <button
                                data-testid="timeline-duplicate-button"
                                onClick={onDuplicate}
                                title="Duplicate clip"
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/15 text-slate-200 hover:border-amberx hover:text-amberx"
                            >
                                <Copy className="h-3.5 w-3.5" />
                            </button>
                            <button
                                data-testid="timeline-delete-button"
                                onClick={onDelete}
                                title="Delete clip"
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/15 text-slate-200 hover:border-emberx hover:text-emberx"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div
                ref={scrollRef}
                data-testid="timeline-scroller"
                className="no-scrollbar overflow-x-auto px-3 pb-3 pt-1"
            >
                <div
                    ref={contentRef}
                    data-testid="timeline-track"
                    className="relative select-none"
                    style={{ width: Math.max(duration * pxs + 40, 320) }}
                    onPointerDown={startSeek}
                    onPointerMove={onMove}
                    onPointerUp={onUp}
                    onPointerCancel={onUp}
                >
                    {/* ruler */}
                    <div className="relative h-5" style={{ width: duration * pxs }}>
                        {ticks.map((t) => (
                            <span
                                key={t}
                                className="absolute top-0 font-mono text-[9px] text-slate-500"
                                style={{ left: t * pxs + 2 }}
                            >
                                {fmtTime(t).slice(0, 5)}
                            </span>
                        ))}
                    </div>

                    {/* clips */}
                    <div className="relative flex h-16 items-stretch gap-1.5" style={{ width: duration * pxs }}>
                        {clips.map((clip, idx) => {
                            const w = Math.max(0.3, clip.trimEnd - clip.trimStart) * pxs;
                            const selected = clip.id === selectedId;
                            return (
                                <div
                                    key={clip.id}
                                    data-testid={`timeline-clip-${idx}`}
                                    className="relative"
                                    style={{ width: w, minWidth: 10 }}
                                    onPointerDown={(e) => startMove(e, idx)}
                                >
                                    <div
                                        className={`h-full overflow-hidden rounded-lg border-2 bg-obsidian ${
                                            selected ? "border-amberx" : "border-white/15"
                                        }`}
                                    >
                                        <div className="flex h-full w-full">
                                            {(clip.thumbs || []).map((th, i) => (
                                                <img
                                                    key={i}
                                                    src={th}
                                                    alt=""
                                                    draggable={false}
                                                    className="h-full min-w-0 flex-1 object-cover"
                                                />
                                            ))}
                                            {!clip.thumbs || clip.thumbs.length === 0 ? (
                                                <div className="flex h-full w-full items-center justify-center">
                                                    <GripVertical className="h-4 w-4 text-slate-600" />
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <span className="pointer-events-none absolute bottom-0.5 left-1 rounded bg-obsidian/75 px-1 font-mono text-[8px] text-slate-300">
                                        {fmtTime(clip.trimEnd - clip.trimStart).slice(0, 5)}
                                    </span>
                                    {clip.transition && clip.transition.id !== "none" && (
                                        <span className="pointer-events-none absolute left-1 top-0.5 rounded bg-amberx/90 px-1 font-mono text-[8px] font-bold text-obsidian">
                                            {clip.transition.label || clip.transition.id}
                                        </span>
                                    )}
                                    {selected && (
                                        <>
                                            <div
                                                data-testid="timeline-trim-start-handle"
                                                onPointerDown={(e) => startTrim(e, idx, "start")}
                                                className="absolute -left-1.5 top-0 z-10 flex h-full w-4 cursor-col-resize items-center justify-center rounded-md bg-amberx"
                                            >
                                                <span className="h-6 w-0.5 rounded bg-obsidian" />
                                            </div>
                                            <div
                                                data-testid="timeline-trim-end-handle"
                                                onPointerDown={(e) => startTrim(e, idx, "end")}
                                                className="absolute -right-1.5 top-0 z-10 flex h-full w-4 cursor-col-resize items-center justify-center rounded-md bg-amberx"
                                            >
                                                <span className="h-6 w-0.5 rounded bg-obsidian" />
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* playhead */}
                    <div
                        ref={headElRef}
                        data-testid="timeline-playhead"
                        className="pointer-events-none absolute top-0 z-20 h-full"
                        style={{ left: 0 }}
                    >
                        <div className="h-full w-0.5 bg-amberx" />
                        <div className="absolute -top-0.5 -left-1.5 h-3 w-3.5 rounded-sm bg-amberx" />
                    </div>
                </div>
            </div>
        </div>
    );
}
